import React, { useMemo, useState, Component } from 'react';
import { Card } from '../SubscriptionAppCard';
import { ChevronDown, X } from 'lucide-react';
// ─── Types ──────────────────────────────────────────────────────────────────
export type JourneyEntryKind = 'joined' | 'renewal' | 'event' | 'nextBilling';
export interface JourneyEntry {
  /** ISO date string used for chronological ordering. */
  date: string;
  kind: JourneyEntryKind;
  /** Primary label, e.g. "Payment recovered", "Paused", "Joined". */
  label: string;
  /** Optional secondary detail shown muted under the label. */
  sub?: string;
}
/**
 * Loyalty milestones are sourced separately from the lifecycle entries.
 * `earned` milestones sit in the past; `upcoming` sit in the future. Loyalty
 * data isn't live yet, so callers render from sample milestone data.
 */
export interface LoyaltyMilestone {
  /** ISO date string. */
  date: string;
  /** Primary label, e.g. "4yr bonus". */
  label: string;
  state: 'earned' | 'upcoming';
}
export interface MembershipJourneyData {
  /** All raw entries between (and including) join and next billing, chronological. */
  entries: JourneyEntry[];
  /** Loyalty milestones (earned + upcoming). Optional — omit when none. */
  loyalty?: LoyaltyMilestone[];
  /** Footnote shown under the track. */
  note?: string;
}
// ─── Display node shape (after collapsing) ───────────────────────────────────
type NodeType = 'joined' | 'renewals' | 'event' | 'loyaltyEarned' | 'loyaltyUpcoming' | 'nextBilling';
interface DisplayNode {
  label: string;
  sub: string;
  type: NodeType;
  /** When true this node is in the future portion of the track. */
  future?: boolean;
}
// Design-system hues distinguish node types; shape + fill add a second cue.
const INK = '#0F172A';
const MUTED = '#94A3B8';
const LINE = '#CBD5E1';
// Ocean Design System accents.
const OCEAN = '#0EA5E9'; // lifecycle anchor + next billing (primary)
const LOYALTY = '#8B5CF6'; // loyalty milestone stars (purple)
const RENEWAL = '#CBD5E1'; // routine renewal dots (neutral)
const MAX_NODES = 5;
// ─── Date helpers ─────────────────────────────────────────────────────────────
function monthYear(date: string): string {
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric'
  });
}
function shortDate(date: string): string {
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}
function tenureFrom(joinedDate: string): string {
  const start = new Date(joinedDate);
  const now = new Date();
  let months = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
  if (months < 0) months = 0;
  const years = Math.floor(months / 12);
  const remMonths = months % 12;
  if (years >= 1) {
    return remMonths > 0 ? `${years} yr${years > 1 ? 's' : ''} ${remMonths} mo` : `${years} yr${years > 1 ? 's' : ''}`;
  }
  return `${months} mo`;
}
/** Whole years elapsed between two dates (years completed, min 1). */
function yearsBetween(fromDate: string, toDate: string): number {
  const from = new Date(fromDate);
  const to = new Date(toDate);
  let years = to.getFullYear() - from.getFullYear();
  const beforeAnniversary = to.getMonth() < from.getMonth() || to.getMonth() === from.getMonth() && to.getDate() < from.getDate();
  if (beforeAnniversary) years -= 1;
  return Math.max(1, years);
}
/** Relative future label, e.g. "in 2 months" / "in 1 year". */
function relativeFuture(date: string): string {
  const target = new Date(date);
  const now = new Date();
  let months = (target.getFullYear() - now.getFullYear()) * 12 + (target.getMonth() - now.getMonth());
  if (target.getDate() < now.getDate()) months -= 1;
  if (months <= 0) return 'soon';
  if (months < 12) return `in ${months} month${months === 1 ? '' : 's'}`;
  const years = Math.floor(months / 12);
  return `in ${years} year${years === 1 ? '' : 's'}`;
}
/** Same calendar month + year — used to merge a milestone onto an anniversary. */
function sameMonth(a: string, b: string): boolean {
  const da = new Date(a);
  const db = new Date(b);
  return da.getFullYear() === db.getFullYear() && da.getMonth() === db.getMonth();
}
/**
 * Build the capped set of display nodes (max 5).
 *  - First node: rolled-up "Joined" anchor with tenure + total renewals.
 *  - Last node: Next billing (hollow dot).
 *  - Middle nodes (budget = 3): most-recent past detail (events, earned
 *    loyalty milestones, collapsed renewal runs) PLUS upcoming loyalty
 *    milestones, prioritising recent/near activity.
 *  - A loyalty milestone that coincides with an anniversary event is merged
 *    into that single node (the anniversary becomes the bonus node).
 */
export function buildJourneyNodes(entries: JourneyEntry[], loyalty: LoyaltyMilestone[] = []): {
  nodes: DisplayNode[];
  hasLoyalty: boolean;
} {
  const now = Date.now();
  const joined = entries.find(e => e.kind === 'joined');
  const nextBilling = entries.find(e => e.kind === 'nextBilling');
  const joinedDate = joined?.date || '';
  // Chronological middle = everything that isn't the join anchor / next billing.
  const middle = entries.filter(e => e.kind === 'renewal' || e.kind === 'event').slice().sort((a, b) => +new Date(a.date) - +new Date(b.date));
  const totalRenewals = middle.filter(e => e.kind === 'renewal').length;
  const earned = loyalty.filter(m => m.state === 'earned');
  const upcoming = loyalty.filter(m => m.state === 'upcoming');
  // A milestone that lands in the same month as an anniversary event merges
  // into it; track which event dates have an attached bonus.
  const mergedEventDates = new Set<string>();
  // Collapse consecutive renewals into single "run" candidates; events and
  // earned loyalty milestones stand alone. Each candidate carries its date so
  // we can place it relative to "now" (solid vs dashed track).
  type Candidate = {
    kind: 'event';
    entry: JourneyEntry;
    bonusLabel?: string;
    date: number;
  } | {
    kind: 'loyaltyEarned';
    milestone: LoyaltyMilestone;
    date: number;
  } | {
    kind: 'renewals';
    count: number;
    date: number;
  };
  const candidates: Candidate[] = [];
  for (const e of middle) {
    if (e.kind === 'event') {
      const isAnniversary = /anniversary|bonus/i.test(e.label);
      // Merge an earned milestone that coincides with this anniversary.
      const coinciding = isAnniversary && earned.find(m => sameMonth(m.date, e.date));
      if (coinciding) mergedEventDates.add(coinciding.date);
      candidates.push({
        kind: 'event',
        entry: e,
        bonusLabel: coinciding?.label,
        date: +new Date(e.date)
      });
    } else {
      const last = candidates[candidates.length - 1];
      if (last && last.kind === 'renewals') {
        last.count += 1;
        last.date = +new Date(e.date);
      } else {
        candidates.push({
          kind: 'renewals',
          count: 1,
          date: +new Date(e.date)
        });
      }
    }
  }
  // Standalone earned loyalty milestones (those not merged into an anniversary).
  for (const m of earned) {
    if (mergedEventDates.has(m.date)) continue;
    candidates.push({
      kind: 'loyaltyEarned',
      milestone: m,
      date: +new Date(m.date)
    });
  }
  // Upcoming loyalty milestones (future, before next billing).
  type FutureCandidate = {
    kind: 'loyaltyUpcoming';
    milestone: LoyaltyMilestone;
    date: number;
  };
  const futureCandidates: FutureCandidate[] = upcoming.map(m => ({
    kind: 'loyaltyUpcoming',
    milestone: m,
    date: +new Date(m.date)
  }));
  // Merge past + future candidates and sort chronologically.
  const allMiddle = [...candidates, ...futureCandidates].sort((a, b) => a.date - b.date);
  // Budget for middle nodes = total cap minus the fixed start + end anchors.
  const middleBudget = MAX_NODES - 2;
  // Prioritise recent/near: keep the last `middleBudget` candidates, but always
  // retain upcoming loyalty milestones (a reason to reach out).
  let keptMiddle = allMiddle;
  if (allMiddle.length > middleBudget) {
    const upcomingKept = allMiddle.filter(c => c.kind === 'loyaltyUpcoming');
    const rest = allMiddle.filter(c => c.kind !== 'loyaltyUpcoming');
    const restBudget = Math.max(0, middleBudget - upcomingKept.length);
    const restKept = rest.slice(rest.length - restBudget);
    keptMiddle = [...restKept, ...upcomingKept].sort((a, b) => a.date - b.date);
  }
  const startNode: DisplayNode = {
    label: `Joined ${monthYear(joinedDate)}`,
    sub: `${tenureFrom(joinedDate)} · ${totalRenewals} renewal${totalRenewals === 1 ? '' : 's'}`,
    type: 'joined'
  };
  const middleNodes: DisplayNode[] = keptMiddle.map(c => {
    if (c.kind === 'event') {
      // Anniversary labels are computed from tenure so they always match the
      // member's actual year count (e.g. "4yr anniversary"), never hardcoded.
      const isAnniversary = /anniversary|bonus/i.test(c.entry.label);
      const tenureLabel = isAnniversary && joinedDate ? `${yearsBetween(joinedDate, c.entry.date)}yr` : '';
      // A merged anniversary becomes the bonus node (filled star).
      if (c.bonusLabel) {
        return {
          label: tenureLabel ? `${tenureLabel} bonus earned` : c.bonusLabel,
          sub: monthYear(c.entry.date),
          type: 'loyaltyEarned'
        };
      }
      return {
        label: tenureLabel ? `${tenureLabel} anniversary` : c.entry.label,
        sub: c.entry.sub || shortDate(c.entry.date),
        type: 'event'
      };
    }
    if (c.kind === 'loyaltyEarned') {
      const tenureLabel = joinedDate ? `${yearsBetween(joinedDate, c.milestone.date)}yr` : '';
      return {
        label: tenureLabel ? `${tenureLabel} bonus earned` : c.milestone.label,
        sub: monthYear(c.milestone.date),
        type: 'loyaltyEarned'
      };
    }
    if (c.kind === 'loyaltyUpcoming') {
      const tenureLabel = joinedDate ? `${yearsBetween(joinedDate, c.milestone.date)}yr` : '';
      return {
        label: tenureLabel ? `${tenureLabel} bonus` : c.milestone.label,
        sub: relativeFuture(c.milestone.date),
        type: 'loyaltyUpcoming',
        future: true
      };
    }
    return {
      label: 'Renewals',
      sub: `×${c.count}`,
      type: 'renewals'
    };
  });
  const endNode: DisplayNode = {
    label: nextBilling?.label || 'Next billing',
    // Relative label computed from today, so it always agrees with the modal.
    sub: nextBilling ? relativeFuture(nextBilling.date) : '',
    type: 'nextBilling',
    future: true
  };
  const nodes = [startNode, ...middleNodes, endNode];
  const hasLoyalty = nodes.some(n => n.type === 'loyaltyEarned' || n.type === 'loyaltyUpcoming');
  return {
    nodes,
    hasLoyalty
  };
}
// ─── Node marker ──────────────────────────────────────────────────────────────
/** Renders the marker (dot or star) for a node, coloured by node type. */
function NodeMarker({
  type
}: {
  type: NodeType;
}) {
  if (type === 'loyaltyEarned') {
    // Filled star ★ — purple loyalty accent.
    return <span className="text-[18px] leading-none" style={{
      color: LOYALTY
    }} aria-hidden="true" data-id="element-1594" data-mp="t759mppqugpm">
        ★
      </span>;
  }
  if (type === 'loyaltyUpcoming') {
    // Hollow star ☆ — purple loyalty accent.
    return <span className="text-[18px] leading-none" style={{
      color: LOYALTY
    }} aria-hidden="true" data-id="element-1595" data-mp="o9v8icszsgly">
        ☆
      </span>;
  }
  // Lifecycle / renewal / next-billing dots.
  const hollow = type === 'nextBilling';
  const dotColor = type === 'renewals' ? RENEWAL : OCEAN;
  return <span className="w-4 h-4 rounded-full border-2 block" style={{
    borderColor: dotColor,
    backgroundColor: hollow ? '#FFFFFF' : dotColor
  }} data-id="element-1596" data-mp="fb9v0w2dnrs1" />;
}
// ─── Component ────────────────────────────────────────────────────────────────
interface MembershipJourneyProps {
  data: MembershipJourneyData;
  // Slim strip that starts collapsed and expands to show the track
  collapsible?: boolean;
}
export function MembershipJourney({
  data,
  collapsible = false
}: MembershipJourneyProps) {
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const isExpanded = !collapsible || isOpen;
  const {
    nodes,
    hasLoyalty
  } = useMemo(() => buildJourneyNodes(data.entries, data.loyalty), [data.entries, data.loyalty]);
  // First future node index — the track is solid up to it, dashed from there on.
  const firstFutureIndex = useMemo(() => {
    const idx = nodes.findIndex(n => n.future);
    return idx === -1 ? nodes.length : idx;
  }, [nodes]);
  // Combined chronological history (lifecycle + loyalty) for the modal.
  // A loyalty bonus that shares a date with an anniversary event is merged into
  // a single filled-violet entry, matching the merged node the track shows.
  const fullHistory = useMemo(() => {
    interface HistoryRow {
      date: string;
      kind: JourneyEntryKind | 'loyalty';
      state: 'earned' | 'upcoming' | undefined;
      label: string;
      sub: string | undefined;
    }
    const loyalty = data.loyalty || [];
    // Anniversary event dates that have a coinciding loyalty bonus; those
    // milestones get folded into the anniversary entry rather than duplicated.
    const mergedMilestoneDates = new Set<string>();
    const lifecycleEntries: HistoryRow[] = data.entries.map(e => {
      const isAnniversary = /anniversary|bonus/i.test(e.label);
      const coinciding = isAnniversary && loyalty.find(m => sameMonth(m.date, e.date));
      if (coinciding) {
        mergedMilestoneDates.add(coinciding.date);
        // Becomes a single filled violet star reading "Nyr anniversary bonus".
        return {
          date: e.date,
          kind: 'loyalty',
          state: 'earned',
          label: 'anniversary bonus',
          sub: e.sub
        };
      }
      return {
        date: e.date,
        kind: e.kind,
        state: undefined,
        label: e.label,
        sub: e.sub
      };
    });
    const loyaltyEntries: HistoryRow[] = loyalty.filter(m => !mergedMilestoneDates.has(m.date)).map(m => ({
      date: m.date,
      kind: 'loyalty',
      state: m.state,
      label: m.label,
      sub: undefined
    }));
    return [...lifecycleEntries, ...loyaltyEntries].sort((a, b) => +new Date(b.date) - +new Date(a.date));
  }, [data.entries, data.loyalty]);
  const joinedDate = useMemo(() => data.entries.find(e => e.kind === 'joined')?.date || '', [data.entries]);
  // Anniversary labels are computed from tenure so they always match the
  // member's actual year count (e.g. "4yr anniversary"), never hardcoded.
  const labelFor = (label: string, date: string) => {
    if (/anniversary|bonus/i.test(label) && joinedDate) {
      const yr = yearsBetween(joinedDate, date);
      const isAnniversary = /anniversary/i.test(label);
      const isBonus = /bonus/i.test(label);
      // Merged anniversary + bonus entry → "Nyr anniversary bonus".
      if (isAnniversary && isBonus) return `${yr}yr anniversary bonus`;
      return isBonus ? `${yr}yr bonus` : `${yr}yr anniversary`;
    }
    return label;
  };
  return <>
      <Card padding="none" data-id="element-1597" data-mp="2kgikubvf4csx">
        <div className={`${collapsible ? 'px-6 py-3' : 'p-6'} ${isExpanded ? 'border-b border-[#E2E8F0]' : 'rounded-lg'} bg-gradient-to-r from-[#F8FAFC] to-white`} data-id="element-1598" data-mp="2kgs31da81fo2">
          <div className="flex items-center justify-between gap-4" data-id="element-1599" data-mp="2aqpatfaxc5qs">
            {collapsible ? <button type="button" onClick={() => setIsOpen(open => !open)} aria-expanded={isOpen} className="flex items-center gap-2 text-left" data-id="element-1600" data-mp="2yeqgmggpx82i">
                <ChevronDown className={`w-4 h-4 text-[#94A3B8] transition-transform ${isOpen ? 'rotate-180' : ''}`} data-id="element-1601" data-mp="33djs63cg2p01" />
                <span className="text-sm font-semibold text-[#0F172A]" data-id="element-1602" data-mp="2pgwhyyvhe2c6">
                  Membership journey
                </span>
              </button> : <h3 className="text-xl font-semibold text-[#0F172A]" data-id="element-1603" data-mp="1dz55rpowfe7p">
                Membership journey
              </h3>}
            <button onClick={() => setIsHistoryOpen(true)} className="text-[13px] text-[#0EA5E9] hover:text-[#0284C7] font-medium transition-colors" data-id="element-1604" data-mp="1i2jporfykeqf">
              View full history
            </button>
          </div>
        </div>
        {isExpanded && <div className="p-6 bg-white rounded-b-lg" data-id="element-1605" data-mp="3axuds4dpdg8e">
          <div className="relative" data-id="element-1606" data-mp="3aw9jmfvavzb2">
            {/* Track — solid for the past, dashed for the future segment. */}
            <div className="absolute top-[8px] left-0 right-0 flex" aria-hidden="true" data-id="element-1607" data-mp="35yhjiyki56vs">
              {nodes.slice(0, -1).map((_, i) => {
              // Segment i connects node i → node i+1. It is "future" once the
              // node it leads into is in the future portion.
              const isFuture = i + 1 >= firstFutureIndex;
              return <div key={i} className="flex-1 h-0" data-id="element-1608" data-mp="2n0ewuqfffm15" data-mpi={i}>
                    {isFuture ? <div className="h-0 border-t-2 border-dashed" style={{
                  borderColor: LINE
                }} data-id="element-1609" data-mp="2n29m2gxqe0b1" /> : <div className="h-0.5" style={{
                  backgroundColor: LINE
                }} data-id="element-1610" data-mp="l8ilbgt9ec6d" />}
                  </div>;
            })}
            </div>
            <div className="relative grid gap-2" style={{
            gridTemplateColumns: `repeat(${nodes.length}, minmax(0, 1fr))`
          }} data-id="element-1611" data-mp="tpan4491ba6a">
              {nodes.map((node, i) => <div key={i} className="flex flex-col items-center text-center" data-id="element-1612" data-mp="12j5rha2c0wkv" data-mpi={i}>
                  <div className="h-5 flex items-center justify-center" data-id="element-1613" data-mp="17h79dro0upur">
                    <NodeMarker type={node.type} data-id="element-1614" data-mp="aqomeqhfjl2w" />
                  </div>
                  <div className="mt-2.5 text-sm font-medium text-[#0F172A]" data-id="element-1615" data-mp="asrklxsqkkip">
                    {node.label}
                  </div>
                  <div className="text-xs mt-0.5 text-[#94A3B8]" data-id="element-1616" data-mp="jrmsdebfi2o4">
                    {node.sub}
                  </div>
                </div>)}
            </div>
          </div>

          {/* Legend — loyalty star entries only when a loyalty node is present. */}
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-6" data-id="element-1617" data-mp="1v8mgdxqqtvcc">
            <div className="flex items-center gap-1.5" data-id="element-1618" data-mp="204bmyl16kci5">
              <span className="w-2.5 h-2.5 rounded-full" style={{
              backgroundColor: OCEAN
            }} data-id="element-1619" data-mp="2l9dwxs39u2ob" />
              <span className="text-xs text-[#475569]" data-id="element-1620" data-mp="2h8v723kxv6or">Lifecycle anchor</span>
            </div>
            {hasLoyalty && <>
                <div className="flex items-center gap-1.5" data-id="element-1621" data-mp="2v11n7po3ddo7">
                  <span className="text-[13px] leading-none" style={{
                color: LOYALTY
              }} aria-hidden="true" data-id="element-1622" data-mp="2q8i6wky0s4fp">
                    ★
                  </span>
                  <span className="text-xs text-[#475569]" data-id="element-1623" data-mp="2zytkz0bf3u0b">
                    Loyalty milestone earned
                  </span>
                </div>
                <div className="flex items-center gap-1.5" data-id="element-1624" data-mp="3dxt2lo0ij8mz">
                  <span className="text-[13px] leading-none" style={{
                color: LOYALTY
              }} aria-hidden="true" data-id="element-1625" data-mp="38yzwwhrgwlxd">
                    ☆
                  </span>
                  <span className="text-xs text-[#475569]" data-id="element-1626" data-mp="1elgsqp2xm533">
                    Loyalty milestone upcoming
                  </span>
                </div>
              </>}
            <div className="flex items-center gap-1.5" data-id="element-1627" data-mp="3qan9l86p9zap">
              <span className="w-2.5 h-2.5 rounded-full" style={{
              backgroundColor: LINE
            }} data-id="element-1628" data-mp="3v6lrqe1fql2o" />
              <span className="text-xs text-[#475569]" data-id="element-1629" data-mp="3hfqs7scttgbr">Routine renewals</span>
            </div>
          </div>

          {data.note && <p className="text-sm text-[#94A3B8] mt-6 pt-5 border-t border-[#E2E8F0]" data-id="element-1630" data-mp="3lfr8lijhk1lg">
              {data.note}
            </p>}
        </div>}
      </Card>

      {/* Full history modal */}
      {isHistoryOpen && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#0F172A]/40" onClick={() => setIsHistoryOpen(false)} role="dialog" aria-modal="true" aria-label="Full membership history" data-id="element-1631" data-mp="3cet1ushnxfz6">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()} data-id="element-1632" data-mp="wq1drgg1bwjl">
            <div className="flex items-center justify-between p-6 border-b border-[#E2E8F0]" data-id="element-1633" data-mp="10qla4hssaw4o">
              <h3 className="text-xl font-semibold text-[#0F172A]" data-id="element-1634" data-mp="3h3mskbugxdoa">
                Membership history
              </h3>
              <button onClick={() => setIsHistoryOpen(false)} className="w-9 h-9 flex items-center justify-center rounded-md hover:bg-[#F1F5F9] transition-colors" aria-label="Close" data-id="element-1635" data-mp="3lw63t25onwpx">
                <X className="w-5 h-5 text-[#475569]" data-id="element-1636" data-mp="3c5uxxa2fvdge" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto" data-id="element-1637" data-mp="3q1hswc0s3zv">
              <ol className="relative border-l border-[#E2E8F0] ml-2 space-y-6" data-id="element-1638" data-mp="3n5l1bbwrld0">
                {fullHistory.map((entry, i) => {
              const isLoyalty = entry.kind === 'loyalty';
              const isUpcoming = entry.state === 'upcoming';
              const isHollow = entry.kind === 'nextBilling' || isUpcoming;
              // Upcoming milestones and next billing share the same
              // relative-time logic as the track, so labels always agree.
              const isFutureRelative = isUpcoming || entry.kind === 'nextBilling';
              return <li key={i} className="ml-6" data-id="element-1639" data-mp="11h7w6h2n2xjj" data-mpi={i}>
                      {isLoyalty ? <span className="absolute -left-[10px] text-[16px] leading-none bg-white" style={{
                  color: LOYALTY
                }} aria-hidden="true" data-id="element-1640" data-mp="wjogdcqamcph">
                          {isUpcoming ? '☆' : '★'}
                        </span> : <span className="absolute -left-[7px] w-3.5 h-3.5 rounded-full border-2 bg-white" style={{
                  borderColor: entry.kind === 'renewal' ? RENEWAL : OCEAN,
                  backgroundColor: isHollow ? '#FFFFFF' : entry.kind === 'renewal' ? RENEWAL : OCEAN
                }} data-id="element-1641" data-mp="16ix6tvly9973" />}
                      <div className="text-sm font-medium text-[#0F172A]" data-id="element-1642" data-mp="rlmoq3esx52e">
                        {labelFor(entry.label, entry.date)}
                      </div>
                      <div className="text-xs text-[#94A3B8] mt-0.5" data-id="element-1643" data-mp="3v8mzbr1xh25s">
                        {entry.sub ? `${entry.sub} · ` : ''}
                        {isFutureRelative ? relativeFuture(entry.date) : shortDate(entry.date)}
                      </div>
                    </li>;
            })}
              </ol>
            </div>
          </div>
        </div>}
    </>;
}